import { memo, useEffect, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import type {
	JiraLongRunningTaskStatus,
	ProcessResponse$key,
} from '@atlassian/jira-relay/src/__generated__/ProcessResponse.graphql';
import { getAnalyticsMeta } from '../../../../common/utils.tsx';
import { useBulkOperationsFormSubmissionActions } from '../../../../controllers/use-form-submit-state/main.tsx';

export type RetryStatus = 'maxAttemptsReached' | 'maxTaskErrorAttemptsReached' | 'underLimit';

export const ANALYTICS_ID = 'bulkEditProgressPolling';
const CANCELLED = 'CANCELLED' as const;
const DEAD = 'DEAD' as const;
const FAILED = 'FAILED' as const;
const COMPLETE = 'COMPLETE' as const;
export const ERROR_STATUSES: Array<JiraLongRunningTaskStatus | null | undefined> = [
	CANCELLED,
	DEAD,
	FAILED,
];
type Props = {
	fragmentRef: ProcessResponse$key;
	onStopRetry: () => void;
	retryStatus: RetryStatus;
};

export const ProcessResponse = memo(({ fragmentRef, onStopRetry, retryStatus }: Props) => {
	const { setError, setSuccess, updateProgress, setPartialError } =
		useBulkOperationsFormSubmissionActions();
	const data = useFragment(
		graphql`
			fragment ProcessResponse on JiraIssueBulkOperationProgress {
				progress
				status
				totalIssueCount
				failedAccessibleIssues {
					edges {
						node {
							issue {
								issueId
								key
							}
						}
					}
				}
			}
		`,
		fragmentRef,
	);

	const { status, totalIssueCount, progress, failedAccessibleIssues } = data;

	const { failedIds, failedKeys } = useMemo(() => {
		const initial: { failedIds: number[]; failedKeys: string[] } = {
			failedIds: [],
			failedKeys: [],
		};

		return (failedAccessibleIssues?.edges || []).reduce((acc, curr) => {
			const issue = curr?.node?.issue;

			if (issue?.issueId) {
				acc.failedIds.push(Number(issue.issueId));
			}

			if (issue?.key) {
				acc.failedKeys.push(issue.key);
			}

			return acc;
		}, initial);
	}, [failedAccessibleIssues]);

	useEffect(() => {
		if (retryStatus !== 'underLimit') {
			return;
		}

		const isProgressFinished = status === COMPLETE || ERROR_STATUSES.includes(status);
		const isPartialError =
			(status === FAILED || status === COMPLETE) &&
			totalIssueCount &&
			failedAccessibleIssues?.edges?.length;

		if (status === COMPLETE && !totalIssueCount) {
			fireErrorAnalytics({
				meta: getAnalyticsMeta(ANALYTICS_ID),
				error: new Error(
					'jira.bulkOperationProgress.totalIssueCount is 0, null or undefined on task complete',
				),
				attributes: {
					totalIssueCount,
					failures: JSON.stringify(failedAccessibleIssues),
				},
			});
		}

		if (isProgressFinished) {
			if (isPartialError) {
				const failedCount = failedIds.length;
				setPartialError(totalIssueCount, failedCount, failedKeys, failedIds.map(Number));
			} else if (status !== COMPLETE) {
				setError();
			}

			onStopRetry();

			if (!isPartialError && status === COMPLETE) {
				setSuccess(totalIssueCount || 0);
			}
			return;
		}

		if (progress) {
			updateProgress(progress / 100);
		}
	}, [
		failedAccessibleIssues,
		failedAccessibleIssues?.edges?.length,
		failedIds,
		failedKeys,
		onStopRetry,
		progress,
		retryStatus,
		setError,
		setPartialError,
		setSuccess,
		status,
		totalIssueCount,
		updateProgress,
	]);

	return null;
});
